"use client"
import { useChains } from "@opensea/app-kit"
import { useNumberFormatter } from "@opensea/i18n"
import {
  Flex,
  FlexCenter,
  ItemAvatar,
  ItemContent,
  ItemTitle,
  Media,
  NumberDisplay,
  QuantityBadge,
  QuantityInput,
  SkeletonBlock,
  SkeletonLine,
  SpaceBetween,
  TABLE_SIZES,
  TableCell,
  TableRow,
  TextBody,
  TextOverflowTooltip,
  UnstyledButton,
  useTable,
} from "@opensea/ui-kit"
import { Close } from "@opensea/ui-kit/icons"
import { bn } from "@opensea/utils"
import type { FragmentOf } from "gql.tada"
import { readFragment } from "gql.tada"
import {
  Controller,
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form"
import { useTranslations } from "use-intl"
import { InlineSwapButton } from "../../../../../components/InlineSwapButton"
import { Item } from "../../../../../components/Item"
import { OfferAmountInput } from "../../../../../components/OfferAmountInput"
import { RarityBadge } from "../../../../../components/RarityBadge"
import { useSyncFormFields } from "../../../../../hooks/useSyncFormField"
import { calculateOrderTotals } from "../../../../../lib/fees"
import { useBalances } from "../../../../../providers/BalancesProvider"
import {
  ITEM_OFFER_HIGH_SEVERITY_WARNING_THRESHOLD,
  OFFER_MAX_DECIMALS,
  ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE,
} from "../../../constants"
import type { CreateOfferFlowFormValues } from "../../types"
import { CreateOfferFlowTableRowFragment } from "./CreateOfferFlowTableRow.fragment"
import { CREATE_OFFER_FLOW_TABLE_CLASSNAMES } from "./constants"

type Props = {
  item: FragmentOf<typeof CreateOfferFlowTableRowFragment> | undefined
  index: number
  context?: {
    isReview: boolean
  }
}

export const CreateOfferFlowTableRow = ({
  item: itemKey,
  context,
  index,
}: Props) => {
  const t = useTranslations("wallet.CreateOfferFlowTableRow")
  const item = readFragment(CreateOfferFlowTableRowFragment, itemKey)
  const { getNativeCurrency, getOfferCurrency } = useChains()

  const { control, setValue } = useFormContext<CreateOfferFlowFormValues>()
  const { errors } = useFormState({ control })
  const { remove } = useFieldArray({
    keyName: "_id",
    control,
    name: "offers",
  })
  const offers = useWatch({ control, name: "offers" })
  const offer = offers[index]
  const format = useNumberFormatter()
  const isReview = context?.isReview ?? false
  const error = errors.offers?.[index]?.price
  const quantityError = errors.offers?.[index]?.quantity
  const { withoutFees } = calculateOrderTotals(offer ? [offer] : [])
  const { getBalanceOfCurrencyBySymbol } = useBalances()

  const { size } = useTable()
  const imageSize = TABLE_SIZES[size].image
  const isFungible = item?.isFungible
  const bestOffer = item?.bestOffer?.pricePerItem.token
  const bestListing = item?.bestListing?.pricePerItem.token

  const priceWarning =
    bestOffer &&
    offer?.price &&
    offer.price > 0 &&
    bn(offer.price).gt(
      bn(bestOffer.unit)
        .times(1 + ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE)
        .decimalPlaces(OFFER_MAX_DECIMALS),
    )
      ? t("priceAboveBestOffer", {
          price: bn(bestOffer.unit)
            .decimalPlaces(OFFER_MAX_DECIMALS)
            .toString(),
          symbol: bestOffer.symbol,
        })
      : undefined
  // For item only show high sev if you are offering above listing
  const showHighSevPriceListingWarning =
    bestListing &&
    Boolean(priceWarning) &&
    offer?.price &&
    bn(offer.price).gt(bn(bestListing.unit))
  const showHighSevPriceOfferWarning =
    Boolean(priceWarning) &&
    bestOffer &&
    bn(offer.price).gt(
      bn(bestOffer.unit).times(ITEM_OFFER_HIGH_SEVERITY_WARNING_THRESHOLD),
    )

  const errorMessage = showHighSevPriceListingWarning
    ? t("priceAboveBestListing", {
        price: bn(bestListing.unit).toString(),
        symbol: bestListing.symbol,
      })
    : priceWarning

  useSyncFormFields({
    fields: [
      {
        name: `offers.${index}.shouldConfirmPrice`,
        value: Boolean(
          showHighSevPriceListingWarning || showHighSevPriceOfferWarning,
        ),
      },
    ],
    setValue,
  })

  const quantityErrorContent = quantityError && (
    <TextBody color="error-1" size="xs">
      {quantityError.message}
    </TextBody>
  )

  const quantityContent = isFungible ? (
    isReview ? (
      <QuantityBadge className="ml-1">{offer?.quantity}</QuantityBadge>
    ) : (
      <Controller
        control={control}
        name={`offers.${index}.quantity`}
        render={({ field }) => (
          <QuantityInput
            className="ml-1"
            error={Boolean(quantityError)}
            min={0}
            onValueChange={field.onChange}
            size="sm"
            value={field.value}
          />
        )}
      />
    )
  ) : null

  if (!item) {
    return <CreateOfferFlowTableRowSkeleton />
  }
  const offerSymbol = item.chain.offerCurrency.symbol

  const balance = getBalanceOfCurrencyBySymbol({
    chainIdentifier: item.chain.identifier,
    symbol: offerSymbol,
  })

  const tokenBalance = bn(offer?.tokenBalance ?? 0)
  const totalOfferAmount = (offer?.price ?? 0) * (offer?.quantity || 1)
  const hasInsufficientBalance = tokenBalance?.lt(totalOfferAmount)

  const nativeCurrency = getNativeCurrency(item.chain.identifier)
  const offerCurrency = getOfferCurrency(item.chain.identifier)

  const priceInfo =
    (priceWarning && !isReview) || error ? (
      <TextBody
        color={
          priceWarning &&
          !showHighSevPriceListingWarning &&
          !showHighSevPriceOfferWarning &&
          !error
            ? "warning"
            : "error-1"
        }
        size="xs"
      >
        {error ? error.message : errorMessage}
      </TextBody>
    ) : isReview ? null : (
      <TextBody color="text-secondary" size="xs">
        {t("balance")}{" "}
        {format(balance?.quantity ?? 0, {
          display: "standard",
          suffix: offerSymbol,
        })}
      </TextBody>
    )

  const itemLockup = (
    <Item variant="unstyled">
      <ItemAvatar className="rounded" size={imageSize} src={item.imageUrl} />
      <ItemContent>
        <FlexCenter className="gap-2">
          <TextOverflowTooltip>
            <ItemTitle>{item.name || `#${item.tokenId}`}</ItemTitle>
          </TextOverflowTooltip>
          <RarityBadge item={item} />
          <Media greaterThanOrEqual="md">{quantityContent}</Media>
        </FlexCenter>
      </ItemContent>
    </Item>
  )

  const priceContent = isReview ? (
    <TextOverflowTooltip>
      <TextBody>
        <NumberDisplay suffix={offerSymbol} value={offer?.price} />
      </TextBody>
    </TextOverflowTooltip>
  ) : (
    <Flex className="items-center gap-2">
      <OfferAmountInput
        autoFocus={index === 0}
        chain={item.chain.identifier}
        offerIndex={index}
        warning={Boolean(priceWarning)}
      />
      {hasInsufficientBalance && (
        <InlineSwapButton
          initialFromCurrency={{
            symbol: nativeCurrency.symbol,
            contractAddress: nativeCurrency.contractAddress,
            chainIdentifier: item.chain.identifier,
          }}
          initialToCurrency={{
            symbol: offerCurrency.symbol,
            contractAddress: offerCurrency.contractAddress,
            chainIdentifier: item.chain.identifier,
          }}
        />
      )}
    </Flex>
  )

  const showDeleteCell = offers.length > 1 && !isReview
  const deleteButton = (
    <UnstyledButton
      onClick={() => {
        remove(index)
      }}
    >
      <Close fill="interactive-primary-styles" size={20} />
    </UnstyledButton>
  )

  return (
    <>
      <Media lessThan="md">
        <TableRow>
          <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.item}>
            {itemLockup}
          </TableCell>

          <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.price}>
            {isFungible ? quantityContent : priceContent}
          </TableCell>
          {showDeleteCell && (
            <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.remove}>
              {deleteButton}
            </TableCell>
          )}
        </TableRow>

        {quantityErrorContent}

        {isFungible && (
          <TableRow>
            <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.item}>
              <TextBody weight="semibold">{t("offerPrice")}</TextBody>
            </TableCell>

            <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.price}>
              {priceContent}
            </TableCell>
            {showDeleteCell && (
              <TableCell
                className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.remove}
              />
            )}
          </TableRow>
        )}

        <Flex className="justify-end">{priceInfo}</Flex>
      </Media>
      <Media greaterThanOrEqual="md">
        <TableRow>
          <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.item}>
            {itemLockup}
          </TableCell>
          <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.floor}>
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={
                    item.collection?.floorPrice?.pricePerItem.token.symbol
                  }
                  value={item.collection?.floorPrice?.pricePerItem.token.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.bestOffer}>
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={item.bestOffer?.pricePerItem.token.symbol}
                  value={item.bestOffer?.pricePerItem.token.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>

          <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.cost}>
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={item.lastSale?.token.symbol}
                  value={item.lastSale?.token.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.proceeds}>
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay suffix={offerSymbol} value={withoutFees} />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.price}>
            {priceContent}
          </TableCell>
          {showDeleteCell && (
            <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.remove}>
              {deleteButton}
            </TableCell>
          )}
        </TableRow>
        {(Boolean(priceWarning) || error || quantityError) && (
          <SpaceBetween className="w-full">
            <div>{quantityErrorContent}</div>
            <div>{priceInfo}</div>
          </SpaceBetween>
        )}
      </Media>
    </>
  )
}

export const CreateOfferFlowTableRowSkeleton = () => {
  const { size } = useTable()
  const imageSize = TABLE_SIZES[size].image

  return (
    <TableRow>
      <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.item}>
        <Item variant="unstyled">
          <ItemAvatar className="rounded" size={imageSize}>
            <SkeletonBlock />
          </ItemAvatar>
          <ItemContent>
            <Flex className="gap-2">
              <SkeletonLine className="h-4 w-16" />
            </Flex>
          </ItemContent>
        </Item>
      </TableCell>
      <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.floor}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.bestOffer}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.cost}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.proceeds}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={CREATE_OFFER_FLOW_TABLE_CLASSNAMES.price}>
        <SkeletonLine className="h-8 w-full" />
      </TableCell>
    </TableRow>
  )
}
